import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import type { i18n as I18nInstance } from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { claimLanguageRank } from "../utils/language-rank";
import {
  buildAllowedLanguages,
  resolveWidgetLanguage,
} from "../utils/resolve-widget-language";

const STORAGE_KEY = "baback:widget-language";

function readManualLanguage(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}

/**
 * Remember the customer's own pick for the session. Nothing outranks it, so it
 * survives the order locale arriving later in the flow.
 */
export function persistWidgetLanguage(code: string): void {
  sessionStorage.setItem(STORAGE_KEY, code);
  claimLanguageRank("manual");
}

// The widget runs under a HashRouter, so its query normally lives inside the
// hash and only react-router sees it. A hand-written URL puts it before the hash
// instead, which is the shape a merchant testing a locale reaches for first.
function useQueryParam(name: string): string | null {
  const [searchParams] = useSearchParams();
  return (
    searchParams.get(name) ?? new URLSearchParams(window.location.search).get(name)
  );
}

// Reading `i18n.language` back would make the chain self-referential: it holds
// whatever we last applied, so a stronger source that later stops being valid
// would keep its own value alive under the browser label. Ask the detector.
function detectBrowserLanguage(i18n: I18nInstance): string | undefined {
  const detected = i18n.services.languageDetector?.detect();
  if (typeof detected === "string") return detected;
  return Array.isArray(detected) ? detected[0] : undefined;
}

/**
 * Resolves the widget language and applies it. `?lang=` wins, then the storefront
 * locale forwarded by the embed script, then the locale carried by the order
 * (`lang`, resolved back-side from the Shopify customer locale), then browser
 * detection, then the shop's first configured language. Every candidate is kept
 * to the languages the shop actually serves.
 *
 * Call it wherever the relevant candidate becomes available: the layout knows
 * the URL and the browser, the order layout knows the order. A weaker source
 * never overwrites a stronger one already applied.
 */
export function useWidgetLanguage(orderLang?: string | null): void {
  const { shopId } = useParams<{ shopId: string }>();
  const { data: shop } = usePublicShop(shopId!);
  const { i18n } = useTranslation("return-widget-v3");
  const urlLang = useQueryParam("lang");
  const storefrontLang = useQueryParam("storefrontLang");

  const supported = i18n.options.supportedLngs;
  const allowed = buildAllowedLanguages(
    shop?.preferences?.languages,
    supported === false ? undefined : supported,
  );

  const resolved = resolveWidgetLanguage(
    [
      { source: "manual", code: readManualLanguage() },
      { source: "url", code: urlLang },
      { source: "storefront", code: storefrontLang },
      { source: "order", code: orderLang },
      { source: "browser", code: detectBrowserLanguage(i18n) },
    ],
    allowed,
  );

  const source = resolved?.source;
  const code = resolved?.code;

  useEffect(() => {
    if (!source || !code) return;
    if (!claimLanguageRank(source)) return;
    if (code !== i18n.language) i18n.changeLanguage(code);
  }, [source, code, i18n]);
}
